import { convertChannelCasing } from 'lib-shared-api-utils';
import { formatInternational } from 'lib-shared-phone-numbers';
import { Sentry } from 'lib-shared-sentry';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import {
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import { selectIsPrimaryConnection } from 'app/middleware/websocket/selectors/websocket';
import { NotificationPlaySoundType } from 'components/notifications/sagas/notification';
import { channelAttributes } from 'utils/channels';
import { pubSub } from 'utils/pubSub';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import { websocketActions } from '../../../app/middleware/websocket/actions/websocket';
import { notificationActions } from '../../notifications/actions/notification';
import { actions as personalActions } from '../../settings/actions/personal';
import { withConfirmation } from '../../shared/confirmModal/sagas/confirmModal';
import { getRequestID } from '../../shared/util/request';
import { actions } from '../actions/conversation';
import * as conversationAPI from '../api/conversation';
import {
  getAcwNotification,
  getAcwNotificationIdByCsid,
} from '../utils/acwNotification';

const CHANNELS_WITH_LIVENESS = ['pstnphone', 'widgetchat', 'messenger'];
const CHANNELS_WITH_INTERNAL = [
  'pstnphone',
  'voicemail',
  'email',
  'contactform',
  'callback',
  'widgetchat',
  'messenger',
];
const TELEPHONY_BASED_CHANNELS = ['pstnphone', 'voicemail', 'callback'];
const WIDGET_BASED_CHANNELS = ['contactform', 'widgetchat', 'messenger'];
const CHATBOT_BASED_CHANNELS = [
  'adachat',
  'digitalgeniuschat',
  'kindlychat',
  'genericapimessaging',
];

type OfferRequestedBy = {
  phone_number: null | string;
  name: null | string;
  email: null | string;
};
type OfferReceivedNumber = {
  e164_format: string;
  name: string;
};
type OfferQueue = { id: string; name: string };
type OfferWidget = {
  id: string;
  widget_name: string;
};
type OfferType =
  | null
  | 'standard'
  | 'abandoned'
  | 'followup'
  | 'preferredagent';
type OfferReceivedAction = {
  payload: {
    params: {
      csid: number;
      direction: string;
      offer_type: OfferType;
      end_user_connected: boolean;
      is_personal_offer: boolean;
      channel: string;
      requested_by: OfferRequestedBy;
      received_number: null | OfferReceivedNumber;
      queued_at?: null | string;
      timeout?: string;
      email_integration?: {
        sender_name: string;
        spf_sender_email: string;
        email_address: string;
      };
      widget: null | OfferWidget;
      queue: null | OfferQueue;
      facebook_page?: {
        name: string;
      };
      whatsapp_number: null;
      message: null | string;
    };
  };
  meta: { resolve: () => void; reject: () => void };
};
type TransferType = 'transfer_type' | 'blind_agent' | 'assisted_agent';
type TransferOfferReceivedAction = {
  payload: {
    params: {
      channel: string;
      csid: number;
      message: string;
      requested_by: OfferRequestedBy;
      timeout: string;
      transfer_type: TransferType;
    };
  };
  meta: { resolve: () => void; reject: () => void };
};
type OfferTitleOpts = {
  channel: string;
  isInternal: boolean;
  isEndUserConnected: boolean;
  isFollowup: boolean;
  isAbandoned: boolean;
  queueName: string;
};

const {
  notificationAdd,
  notificationRemoveByType,
  notificationRemoveById,
  notificationUpdate,
} = notificationActions;

// Destructure the Conversation actions.
export const {
  conversationGet,
  conversationGetSucceeded,
  conversationGetFailed,
  conversationGetBeforeSucceeded,

  conversationLinkChild,
  conversationLinkChildSucceeded,
  conversationLinkChildFailed,

  conversationSubmitMessage,
  conversationSubmitMessageSucceeded,
  conversationSubmitMessageFailed,
  conversationAddSubmittedMessage,

  conversationRespondeToOfferAccepted,
  conversationRespondeToOfferAcceptedSucceeded,
  conversationRespondeToOfferAcceptedFailed,

  conversationRespondeToOfferRejected,
  conversationRespondeToOfferRejectedSucceeded,
  conversationRespondeToOfferRejectedFailed,

  conversationRespondeToTransferOfferAccepted,
  conversationRespondeToTransferOfferAcceptedSucceeded,
  conversationRespondeToTransferOfferAcceptedFailed,

  conversationRespondeToTransferOfferRejected,
  conversationRespondeToTransferOfferRejectedSucceeded,
  conversationRespondeToTransferOfferRejectedFailed,

  conversationClose,
  conversationCloseSucceeded,
  conversationCloseFailed,
  conversationClaim,
  conversationClaimSucceeded,
  conversationClaimFailed,
  conversationClaimCanceled,
  conversationWsOfferReceived,
  conversationWsOfferCanceled,
  conversationWsTransferOfferReceived,
  conversationAddMessage,
  conversationOutboundStarted,
  conversationAddWrapup,
  conversationAddWrapupSucceeded,
  conversationAddWrapupFailed,
  conversationCompleteAcw,
  conversationCompleteAcwSucceeded,
  conversationCompleteAcwFailed,
  conversationExtendAcw,
  conversationExtendAcwSucceeded,
  conversationExtendAcwFailed,
  conversationTag,
  conversationTagSucceeded,
  conversationTagFailed,
  conversationUntag,
  conversationUntagSucceeded,
  conversationUntagFailed,

  conversationGetOutboundAddress,
  conversationGetOutboundAddressSucceeded,
  conversationGetOutboundAddressFailed,

  conversationSetOutboundAddress,
  conversationSetOutboundAddressSucceeded,
  conversationSetOutboundAddressFailed,

  conversationToPending,
  conversationToPendingSucceeded,
  conversationToPendingFailed,

  conversationCancelPending,
  conversationCancelPendingSucceeded,
  conversationCancelPendingFailed,

  conversationPauseCallRecording,
  conversationPauseCallRecordingSucceeded,
  conversationPauseCallRecordingFailed,

  conversationResumeCallRecording,
  conversationResumeCallRecordingSucceeded,
  conversationResumeCallRecordingFailed,

  conversationWsAddAcw,
  conversationWsEmailDeliveryFailure,

  conversationWsTagged,
  conversationWsUntagged,

  conversationWsQueued,

  conversationWsNewPartyBusy,
  conversationWsNewPartyFailed,
  conversationWsNewPartyNoAnswer,
  conversationWsNewPartyRemoved,

  conversationUnblockAcw,
  conversationToggleFocusMode,
  conversationSetFocusMode,
} = actions;

// Saga
export const conversation = [
  takeLatest(
    conversationToPending.toString() as any,
    genWithPromise(
      conversationAPI.conversationSetToPending,
      conversationToPendingSucceeded,
      conversationToPendingFailed,
    ),
  ),

  takeLatest(
    conversationCancelPending.toString() as any,
    genWithPromise(
      conversationAPI.conversationCancelPending,
      conversationCancelPendingSucceeded,
      conversationCancelPendingFailed,
    ),
  ),

  takeLatest(
    conversationGetOutboundAddress.toString() as any,
    genWithPromise(
      conversationAPI.getConversationOutboundAddress,
      conversationGetOutboundAddressSucceeded,
      conversationGetOutboundAddressFailed,
    ),
  ),

  takeLatest(
    conversationSetOutboundAddress.toString() as any,
    genWithPromise(
      conversationAPI.setConversationOutboundAddress,
      conversationSetOutboundAddressSucceeded,
      conversationSetOutboundAddressFailed,
    ),
  ),
  takeLatest(
    conversationGet.toString() as any,
    genGetConversationWithPromise(
      conversationAPI.getConversation,
      conversationGetSucceeded,
      conversationGetFailed,
    ),
  ),
  takeEvery(
    conversationSubmitMessage.toString() as any,
    genSubmitMessageWithPromise(
      conversationAPI.submitMessage,
      conversationSubmitMessageSucceeded,
      conversationSubmitMessageFailed,
    ),
  ),
  takeEvery(
    conversationPauseCallRecording.toString() as any,
    genWithPromisePauseCallRecording(
      conversationAPI.pauseCallRecording,
      conversationPauseCallRecordingSucceeded,
      conversationPauseCallRecordingFailed,
    ),
  ),
  takeEvery(
    conversationResumeCallRecording.toString() as any,
    genWithPromisePauseCallRecording(
      conversationAPI.resumeCallRecording,
      conversationResumeCallRecordingSucceeded,
      conversationResumeCallRecordingFailed,
    ),
  ),
  takeEvery(
    conversationClose.toString() as any,
    genCloseConversationWithPromise(
      conversationAPI.conversationClose,
      conversationCloseSucceeded,
      conversationCloseFailed,
    ),
  ),
  takeEvery(
    conversationLinkChild.toString() as any,
    genWithPromiseAddLinkConversation(
      conversationAPI.getConversation,
      conversationLinkChildSucceeded,
      conversationLinkChildFailed,
    ),
  ),
  takeEvery(
    conversationAddWrapup.toString() as any,
    genWithPromiseAddProfileWithResponse(
      conversationAPI.addWrapup,
      conversationAddWrapupSucceeded,
      conversationAddWrapupFailed,
    ),
  ),

  takeEvery(
    conversationWsOfferReceived.toString() as any,
    conversationOfferReceived(),
  ),

  takeEvery(
    conversationWsTransferOfferReceived.toString() as any,
    conversationTransferOfferReceived(),
  ),

  takeEvery(
    conversationRespondeToOfferAccepted.toString() as any,
    genWithPromiseResponseToOfferAccepted(
      conversationAPI.responseToOffer,
      conversationRespondeToOfferAcceptedSucceeded,
      conversationRespondeToOfferAcceptedFailed,
    ),
  ),

  takeEvery(
    conversationRespondeToOfferRejected.toString() as any,
    genWithPromiseResponseToOfferRejected(
      conversationAPI.responseToOffer,
      conversationRespondeToOfferRejectedSucceeded,
      conversationRespondeToOfferRejectedFailed,
    ),
  ),

  takeEvery(
    conversationRespondeToTransferOfferAccepted.toString() as any,
    genWithPromiseResponseToTransferOfferAccepted(
      conversationAPI.responseToTransferOffer,
      conversationRespondeToTransferOfferAcceptedSucceeded,
      conversationRespondeToTransferOfferAcceptedFailed,
    ),
  ),

  takeEvery(
    conversationRespondeToTransferOfferRejected.toString() as any,
    genWithPromiseResponseToOfferRejected(
      conversationAPI.responseToTransferOffer,
      conversationRespondeToTransferOfferRejectedSucceeded,
      conversationRespondeToTransferOfferRejectedFailed,
    ),
  ),

  takeLatest(conversationClaim.toString() as any, genClaimConversation()),

  takeEvery(
    conversationTag.toString() as any,
    genWithPromiseAddTag(
      conversationAPI.tagConversation,
      conversationTagSucceeded,
      conversationTagFailed,
    ),
  ),

  takeEvery(
    conversationUntag.toString() as any,
    genWithPromise(
      conversationAPI.untagConversation,
      conversationUntagSucceeded,
      conversationUntagFailed,
    ),
  ),

  takeEvery(
    conversationCompleteAcw.toString() as any,
    genWithPromise(
      conversationAPI.completeAcw,
      conversationCompleteAcwSucceeded,
      conversationCompleteAcwFailed,
    ),
  ),

  takeEvery(
    conversationExtendAcw.toString() as any,
    genWithPromise(
      conversationAPI.extendAcw,
      conversationExtendAcwSucceeded,
      conversationExtendAcwFailed,
    ),
  ),

  takeEvery(
    conversationUnblockAcw.pending.toString() as any,
    genWithPromise(
      conversationAPI.extendAcw,
      conversationUnblockAcw.fulfilled,
      conversationUnblockAcw.rejected,
    ),
  ),

  takeLatest(
    conversationOutboundStarted.toString() as any,
    navigateToConversation(),
  ),

  takeEvery(conversationWsAddAcw.toString(), genConversationWsAddAcw()),

  takeEvery(
    conversationExtendAcwSucceeded.toString(),
    genConversationExtended(),
  ),

  takeEvery(conversationUnblockAcw.fulfilled, genConversationAcwUnblocked()),

  takeEvery(
    conversationRespondeToOfferAcceptedSucceeded.toString(),
    genConversationRespondeToOfferAcceptedSucceeded(),
  ),

  takeEvery(
    conversationClaimSucceeded.toString(),
    genConversationRespondeToOfferAcceptedSucceeded(),
  ),

  takeEvery(
    conversationWsEmailDeliveryFailure.toString(),
    genConversationEmailDeliveryFailure,
  ),
  takeLatest(conversationWsNewPartyBusy.toString(), conversationRemoveCaller),
  takeLatest(conversationWsNewPartyFailed.toString(), conversationRemoveCaller),
  takeLatest(
    conversationWsNewPartyNoAnswer.toString(),
    conversationRemoveCaller,
  ),
  takeEvery(conversationToggleFocusMode, genToggleFocusMode),
];

let focusModeLeftMenuOpenRestoreState = false;
function* genToggleFocusMode(action) {
  const getFocusMode = (state) => state.conversation.isFocusMode;
  const getLeftMenuOpen = (state) => state.settings.personal.isLeftMenuOpen;
  const prevFocusModeState = yield select(getFocusMode);
  const isLeftMenuOpen = yield select(getLeftMenuOpen);
  const nextFocusModeState =
    action.payload !== undefined ? action.payload : !prevFocusModeState;

  if (prevFocusModeState === nextFocusModeState) {
    return;
  }

  yield put(conversationSetFocusMode(nextFocusModeState));

  if (nextFocusModeState) {
    // If we're entering focus mode, close the left menu and custom card menu.
    focusModeLeftMenuOpenRestoreState = isLeftMenuOpen;
    yield put(
      personalActions.personalLeftMenu({
        key: 'isLeftMenuOpen',
        value: false,
      }),
    );
  }

  if (!nextFocusModeState) {
    // If we're leaving focus mode restore menu states.
    if (focusModeLeftMenuOpenRestoreState && !isLeftMenuOpen) {
      yield put(
        personalActions.personalLeftMenu({
          key: 'isLeftMenuOpen',
          value: focusModeLeftMenuOpenRestoreState,
        }),
      );
    }
  }
}

function* conversationRemoveCaller(action: any) {
  yield delay(5000);
  yield put(conversationWsNewPartyRemoved(action.payload));
}

// for other side effects and reducers
function genConversationRespondeToOfferAcceptedSucceeded() {
  return function* (action: any) {};
}

function genConversationExtended() {
  return function* (action: any) {
    const id = getAcwNotificationIdByCsid(action.payload.data.csid);
    const time = action.payload.data.remaining_duration_in_seconds * 1000;
    if (time) {
      yield put(
        notificationUpdate({
          id,
          patch: { updates: { time } },
        }),
      );
    }
  };
}

function genConversationAcwUnblocked() {
  return function* (action: any) {
    if (action.payload.data.remaining_duration_in_seconds > 0) {
      yield put(
        notificationAdd(
          getAcwNotification({
            csid: action.payload.data.csid,
            text: 'You can now end the wrap-up time in conversation',
            expiration: {
              seconds: action.payload.data.remaining_duration_in_seconds,
            },
          }),
        ),
      );
    }
  };
}

function genConversationWsAddAcw() {
  return function* (action: any) {
    const isPrimaryConnectionState = yield select(selectIsPrimaryConnection);

    // Only show wrap up notification if the instance
    // is considered primary connection
    if (!isPrimaryConnectionState) {
      return;
    }

    try {
      const getProfile = (state) => state.settings.profile.profile;
      const profileState = yield select(getProfile);
      if (profileState && action.payload.agent_id === profileState.id) {
        yield put(
          notificationAdd(
            getAcwNotification({
              csid: action.payload.csid,
              expiration: { seconds: action.payload.remaining_duration },
            }),
          ),
        );
      }
    } catch (exception) {
      console.warn('genConversationWsAddAcw', exception);
    }
  };
}

function navigateToConversation() {
  return function* (action: any) {
    pubSub.publish('navigate', {
      path: '/conversation/:csid',
      params: {
        path: { csid: `${action.payload.csid}` },
      },
    });
  };
}

const getOfferReason = (isPersonalOffer: boolean, offerType: string | null) => {
  if (!isPersonalOffer) return '';
  if (offerType === null || offerType === 'standard') {
    return 'Last reply by you';
  }
  if (offerType === 'followup') {
    return 'Marked as follow-up by you';
  }
  if (offerType === 'preferredagent') {
    return 'You had a conversation before';
  }
  return '';
};

const getOfferWaitingTime = (
  isFollowup: boolean,
  isAbandoned: boolean,
  queuedAt?: null | string,
) => {
  if (isFollowup || isAbandoned) return null;
  if (queuedAt !== undefined && queuedAt !== null) {
    return queuedAt;
  }
  return null;
};

const getOfferPlaySound = (
  channel: string,
): NotificationPlaySoundType | null => {
  if (TELEPHONY_BASED_CHANNELS.includes(channel)) {
    return 'offer-ringing-phone';
  }
  if (['email', 'contactform'].includes(channel)) {
    return 'offer-ringing-email';
  }
  if (
    ['widgetchat', 'messenger'].includes(channel) ||
    CHATBOT_BASED_CHANNELS.includes(channel)
  ) {
    return 'offer-ringing-chat';
  }
  if (['facebookmessenger', 'whatsapp', 'sms'].includes(channel)) {
    return 'offer-ringing-messaging';
  }
  return null;
};

const getOfferClassname = (
  channel: string,
  isFollowup: boolean,
  isEndUserConnected: boolean,
) => {
  if (isFollowup) return 'follow-up';
  if (CHANNELS_WITH_LIVENESS.includes(channel) && !isEndUserConnected) {
    return 'closed';
  }

  return 'default';
};

const getOfferIcon = (
  channel: string,
  isFollowup = false,
  isAbandoned = false,
) => {
  if (isFollowup) return 'i-clock-one';
  if (isAbandoned) {
    if (channel === 'pstnphone') return 'phone-hang';
    if (channel === 'contactform') return 'chat-widget';
  }
  if (channel === 'pstnphone') return 'phone';
  if (channel === 'voicemail') return 'voicemail';
  if (channel === 'email') return 'mail';
  if (channel === 'contactform') return 'chat';
  if (channel === 'callback') return 'callback';
  if (channel === 'widgetchat') return 'chat';
  if (channel === 'facebookmessenger') return 'facebook-messenger';
  if (channel === 'whatsapp') return 'whatsapp';
  if (channel === 'sms') return 'sms';
  if (channel === 'messenger') return 'messenger-a';
  if (channel === 'twitter') return 'twitter';
  if (channel === 'adachat') return 'messenger-a';
  if (channel === 'digitalgeniuschat') return 'messenger-a';
  if (channel === 'kindlychat') return 'messenger-a';
  if (channel === 'genericapimessaging') return 'generic-api';
  if (channel === 'trustpilotreviews') return 'trustpilot-outlined';
  if (channel === 'yotporeviews') return 'yotpo';
  return 'info';
};

const getChannelName = (channel: string) => {
  return (
    channelAttributes[convertChannelCasing(channel, 'snake')]?.name ?? 'Unknown'
  );
};

const getChannelPrefix = (
  channel: string,
  isAbandoned: boolean,
  isInternal: boolean,
  isEndUserConnected: boolean,
) => {
  const channelName = getChannelName(channel);

  if (isAbandoned) return `Abandoned ${channelName}`;
  if (CHANNELS_WITH_INTERNAL.includes(channel) && isInternal) {
    return `Internal ${channelName}`;
  }
  if (CHANNELS_WITH_LIVENESS.includes(channel) && !isEndUserConnected) {
    return `Closed ${channelName}`;
  }
  return channelName;
};

const getQueueSuffix = (
  channelName: string,
  queueName: string,
  isInternal: boolean,
) => {
  if (!isInternal && queueName) {
    return `${channelName} in ${queueName}`;
  }
  return channelName;
};

const getOfferTitle = (titleOpts: OfferTitleOpts) => {
  const {
    channel,
    isFollowup,
    isAbandoned,
    isInternal,
    isEndUserConnected,
    queueName,
  } = titleOpts;

  if (isFollowup) return `Conversation to follow up`;

  return getQueueSuffix(
    getChannelPrefix(channel, isAbandoned, isInternal, isEndUserConnected),
    queueName,
    isInternal,
  );
};

const getOfferStatus = (isFollowup: boolean, isAbandoned: boolean) => {
  if (isFollowup) return 'pending';
  if (isAbandoned) return null;
  return 'open';
};

const getOfferTime = (timeout?: null | string) => {
  if (timeout !== undefined) {
    const timeInSec = parseInt(timeout as any, 10);
    if (!isNaN(timeInSec)) {
      return timeInSec * 1000;
    }
  }
  return undefined;
};

const getOfferReqByPhoneNumber = (requestedBy: OfferRequestedBy) => {
  if (requestedBy && isNonEmptyString(requestedBy.phone_number)) {
    const requesterPhoneNumber = requestedBy.phone_number;
    return (
      formatInternational(requesterPhoneNumber || undefined) ||
      requesterPhoneNumber
    );
  }
  return '';
};

const getOfferReceivedNumber = (
  receivedNumber: OfferReceivedNumber,
  requestedBy: OfferRequestedBy,
) => {
  if (receivedNumber) {
    if (receivedNumber.name) return receivedNumber.name;
    if (receivedNumber.e164_format) {
      const receivedNumberE164 = receivedNumber.e164_format;
      return (
        formatInternational(receivedNumberE164) ||
        requestedBy.phone_number ||
        ''
      );
    }
  }
  return '';
};

const isNonEmptyString = (val: any): boolean =>
  typeof val === 'string' && val.trim() !== '';

function conversationOfferReceived() {
  return function* (action: OfferReceivedAction) {
    try {
      const csid = action.payload.params.csid;
      let offerNotificationText = '';
      const { params } = action.payload;
      const {
        channel,
        direction,
        offer_type: offerType,
        is_personal_offer: isPersonalOffer,
        requested_by: requestedBy,
        queue,
        queued_at: queuedAt,
        widget,
        received_number: receivedNumber,
        email_integration: emailIntegration,
        facebook_page: facebookPage,
        timeout,
      } = params;
      const widgetName = (widget && widget.widget_name) || '';
      const isInternal = direction === 'internal';
      const isEndUserConnected = Boolean(params.end_user_connected);
      const time = getOfferTime(timeout);
      const isFollowup = offerType === 'followup';
      const isAbandoned = offerType === 'abandoned';
      const titleOpts: OfferTitleOpts = {
        channel,
        isInternal,
        isEndUserConnected,
        isFollowup,
        isAbandoned,
        queueName: (queue && queue.name) || '',
      };

      if (!isInternal && receivedNumber) {
        const toNumber = getOfferReceivedNumber(receivedNumber, requestedBy);
        offerNotificationText = ` To: ${toNumber}`;
      }

      if (channel === 'email') {
        if (emailIntegration) {
          const { sender_name, spf_sender_email, email_address } =
            emailIntegration;

          if (isNonEmptyString(sender_name)) {
            offerNotificationText += `\n\nTo: ${sender_name}`;
          } else if (isNonEmptyString(spf_sender_email)) {
            offerNotificationText += `\n\nTo: ${spf_sender_email}`;
          } else if (isNonEmptyString(email_address)) {
            offerNotificationText += `\n\nTo: ${email_address}`;
          }
        }
      }

      if (
        WIDGET_BASED_CHANNELS.includes(channel) &&
        isNonEmptyString(widgetName)
      ) {
        offerNotificationText += `\n\nTo: ${widgetName}`;
      }

      if (isNonEmptyString(requestedBy.name)) {
        if (requestedBy.name) {
          offerNotificationText += `\n\n From: ${requestedBy.name}`;
        } else if (
          (channel === 'pstnphone' || channel === 'callback') &&
          requestedBy.phone_number
        ) {
          offerNotificationText += ` (${getOfferReqByPhoneNumber(
            requestedBy,
          )})`;
        }
      } else if (channel === 'email' && isNonEmptyString(requestedBy.email)) {
        offerNotificationText += `\n\n From: ${requestedBy.email}`;
      } else if (
        TELEPHONY_BASED_CHANNELS.includes(channel) &&
        requestedBy.phone_number
      ) {
        offerNotificationText += `\n\n From: ${getOfferReqByPhoneNumber(
          requestedBy,
        )}`;
      }

      if (channel === 'facebookmessenger' && facebookPage) {
        offerNotificationText += `\n\n Facebook Page: ${facebookPage.name}`;
      }

      // Overwrite title if it's a followup channel.
      if (isFollowup && queue && isNonEmptyString(queue.name)) {
        offerNotificationText += `\n\n Queue: ${queue.name}`;
      }

      const createRejectAction = (callbackFn: any) => {
        return function* () {
          yield put(
            notificationRemoveByType({ type: 'conversationOffer_' + csid }),
          );
          yield put(
            conversationRespondeToOfferRejected({
              csid: csid,
              did_accept: false,
              isInternal: isInternal,
              isEndUserConnected: isEndUserConnected,
            }),
          );
          callbackFn({ channel });
        };
      };

      const option = {
        title: getOfferTitle(titleOpts),
        className: getOfferClassname(channel, isFollowup, isEndUserConnected),
        type: 'conversationOffer_' + csid,
        text: offerNotificationText,
        icon: getOfferIcon(channel, isFollowup, isAbandoned),
        time: time,
        waitingTime: getOfferWaitingTime(isFollowup, isAbandoned, queuedAt),
        reason: getOfferReason(isPersonalOffer, offerType),
        playSound: getOfferPlaySound(channel),
        closeAction: createRejectAction(() =>
          ampli.notificationOfferClosed({ channel }),
        ),
        mainAction: {
          navigate: '/search/conversations',
        },
        leftAction: {
          text: 'Accept',
          action: function* () {
            yield put(
              conversationRespondeToOfferAccepted({
                csid: csid,
                did_accept: true,
                isInternal: isInternal,
                isEndUserConnected: isEndUserConnected,
              }),
            );
            yield put(
              notificationRemoveByType({ type: 'conversationOffer_' + csid }),
            );
            ampli.notificationOfferAccepted({ channel });
          },
        },
        rightAction: {
          text: 'Reject',
          action: createRejectAction(() =>
            ampli.notificationOfferRejected({ channel }),
          ),
        },
        conversation: {
          channel,
          direction,
          status: getOfferStatus(isFollowup, isAbandoned),
        },
      };

      yield put(notificationAdd(option));

      // Fix for this backend task https://dixa-product.atlassian.net/browse/TEAMS-9425
      if (time && isInternal) {
        yield delay(time);
        yield put(conversationWsOfferCanceled({ csid: csid }));
      }
    } catch (exception) {
      Sentry.captureException(
        new Error('conversationOfferReceived saga exception'),
        { extra: { exception } },
      );
    }
  };
}

function conversationTransferOfferReceived() {
  return function* (action: TransferOfferReceivedAction) {
    try {
      const csid = action.payload.params.csid;
      const channel = action.payload.params.channel;
      const requestedBy = action.payload.params.requested_by;
      const timeout = action.payload.params.timeout;
      const message = action.payload.params.message;
      const transferType = action.payload.params.transfer_type;
      let requesterName: string | null = '';
      let offerNotificationText = '';
      const time = getOfferTime(timeout);
      let title;
      let icon = getOfferIcon(channel);
      const playSound = getOfferPlaySound(channel);
      let className;
      const phoneNumberFormat = getOfferReqByPhoneNumber(requestedBy);

      if (isNonEmptyString(requestedBy.name)) {
        requesterName = requestedBy.name;
      } else if (phoneNumberFormat) {
        requesterName = phoneNumberFormat;
      } else if (isNonEmptyString(requestedBy.email)) {
        requesterName = requestedBy.email;
      }

      const isBlindOrAssistedTransfer = [
        'blind_agent',
        'assisted_agent',
      ].includes(transferType);

      if (isBlindOrAssistedTransfer) {
        offerNotificationText = `From agent: ${requesterName}`;
      } else {
        offerNotificationText = `From: ${requesterName}`;
      }

      if (
        isNonEmptyString(requestedBy.name) &&
        !isBlindOrAssistedTransfer &&
        TELEPHONY_BASED_CHANNELS.includes(channel) &&
        requestedBy.phone_number &&
        phoneNumberFormat
      ) {
        offerNotificationText += ` (${phoneNumberFormat})`;
      }

      if (message) {
        offerNotificationText += `\n\n${message}`;
      }

      if (channel === 'pstnphone') {
        icon = 'phone-outgoing';
      }

      const lowerChannelName = getChannelName(channel).toLowerCase();
      if (transferType === 'blind_agent') {
        title = `${requesterName} is transferring ${lowerChannelName} to you`;
      } else if (transferType === 'assisted_agent') {
        title = `${requesterName} is requesting assistance on ${lowerChannelName}`;
      } else {
        title = `Transferring ${lowerChannelName} from ${requesterName}`;
      }

      const createRejectAction = (callbackFn: any) => {
        return function* () {
          yield put(
            conversationRespondeToTransferOfferRejected({
              csid: csid,
              did_accept: false,
              channel: channel,
            }),
          );
          yield put(
            notificationRemoveByType({ type: 'conversationOffer_' + csid }),
          );
          callbackFn({ channel });
        };
      };

      const option = {
        title: title,
        className: className,
        type: 'conversationOffer_' + csid,
        text: offerNotificationText,
        icon: icon,
        time: time,
        playSound: playSound,
        closeAction: createRejectAction(() =>
          ampli.notificationTransferOfferClosed({ channel }),
        ),
        actionOnTimeout: () => {
          ampli.notificationTransferOfferTimeout();
        },
        mainAction: {
          navigate: '/search/conversations',
        },
        leftAction: {
          text: 'Accept',
          action: function* () {
            yield put(
              conversationRespondeToTransferOfferAccepted({
                csid: csid,
                did_accept: true,
                channel: channel,
              }),
            );
            yield put(
              notificationRemoveByType({ type: 'conversationOffer_' + csid }),
            );
            ampli.notificationTransferOfferAccepted({ channel });
          },
        },
        rightAction: {
          text: 'Reject',
          action: createRejectAction(() =>
            ampli.notificationTransferOfferRejected({ channel }),
          ),
        },
        conversation: {
          channel,
        },
      };

      yield put(notificationAdd(option));
      // Fix for this backend task https://dixa-product.atlassian.net/browse/TEAMS-3366
      if (time) {
        yield delay(time);
        yield put(conversationWsOfferCanceled({ csid: csid }));
      }
    } catch (exception) {
      console.warn(exception);
    }
  };
}

function genWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromiseAddLinkConversation(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);

      const getProfile = (state) => state.settings.profile.profile;
      const profileState = yield select(getProfile);

      result.request = {
        userID: profileState.id,
      };
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromisePauseCallRecording(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      result.request = {
        csid: action.payload.csid,
      };
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromiseAddTag(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      result.request = {
        payload: action.payload,
      };
      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genClaimConversation() {
  return function* (action: any) {
    const claimFunction = genWithPromiseResponseToOfferAccepted(
      conversationAPI.claimConversation,
      conversationClaimSucceeded,
      conversationClaimFailed,
    );
    const getConversation = (state) => state.conversations.metaConversations;
    const metaConversations = yield select(getConversation);

    let foundConversation: any | null = null;
    metaConversations.forEach((metaConversation) => {
      if (metaConversation.conversation.id === action.payload.csid) {
        foundConversation = metaConversation;
      }
    });
    // We just claim the conversation if there is not one assigned or the conversation is closed.
    if (
      !foundConversation ||
      !foundConversation.conversation.assignment?.agent_id ||
      foundConversation.conversation.is_closed
    ) {
      return yield* claimFunction(action);
    }

    const agent = foundConversation.conversation.assignment.agent;
    const agentName = agent ? agent.name : 'Someone';

    return yield* withConfirmation(
      {
        headline: 'Another agent is working on this conversation',
        text: `${agentName} is already working on this conversation. Are you sure you want to claim this conversation?`,
        confirmText: 'Yes, take over',
      },
      function* () {
        yield* claimFunction(action);
      },
      function* () {
        yield put(conversationClaimCanceled());
      },
    );
  };
}

function genWithPromiseResponseToOfferAccepted(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result?: any) => any,
) {
  return function* (action: any) {
    let result;

    try {
      result = yield call(apiAction, action.payload);
      const isEndUserConnected =
        result?.data?.conversation?.live_assesment?.is_end_user_connected;

      const getProfile = (state) => state.settings.profile.profile;
      const profileState = yield select(getProfile);

      const userID: null | string = profileState?.id || null;
      const acceptedBy: null | User = result?.data?.accepted_by;

      // Check if acceptedBy if the same user as profile.
      if (
        result?.data &&
        result?.meta &&
        acceptedBy &&
        userID === acceptedBy.id
      ) {
        result.meta.userID = userID;

        if (
          result.data.channel === 'pstnphone' ||
          result.data.channel === 'callback'
        ) {
          const conversation = result.data.conversation;
          const getTelephony = (state) => state.telephony;
          const telephonyState = yield select(getTelephony);
          // We test if we should disconnect from the current call.
          if (
            telephonyState &&
            telephonyState.currentCall &&
            isEndUserConnected === true
          ) {
            yield put(
              twilioActions.twilioDisconnectCall({
                csid: telephonyState.currentCall.csid,
              }),
            ); // Disconnect current call.
          }
          if (
            result.data.channel === 'pstnphone' &&
            isEndUserConnected === true
          ) {
            // Only connect to twilio conferrence if there is an enduser connected.
            // Internal (agent-to-agent) calls arrive on the pstnphone channel
            // too, but have no provisioned number — flag them so the middleware
            // skips region resolution (see handleConnectCall in actionHandlers).
            yield put(
              twilioActions.twilioConnectCall({
                csid: result.data.csid,
                isInternal: action.payload?.isInternal === true,
              }),
            ); // Connect to the call to the conferrence.
          } else if (
            result.data.channel === 'callback' &&
            conversation.is_callback_consumed === false
          ) {
            yield put(
              twilioActions.twilioMakeCallback({ csid: result.data.csid }),
            ); // Make a callback call.
          }
        }

        // Navigate to conversation
        pubSub.publish('navigate', {
          path: '/conversation/:csid',
          params: {
            path: { csid: `${result.data.csid}` },
          },
        });

        yield call(action.meta.resolve, result);
        yield put(resultSucceededAction(result));
      } else {
        let title = '';
        let name = '';
        let leftAction: any = undefined;
        let rightAction: any = undefined;
        let mainAction: any = undefined;
        const notificationID = getRequestID();

        if (result.data.status === 'closed') {
          // You are too busy to get a new conversation.
          name = 'The conversation has been closed.';
          title = 'The conversation is closed';
        } else if (acceptedBy === null && result.data.status === 'open') {
          // You are too busy to get a new conversation.
          const getPresence = (state) => state.presence.presence;
          const presenceState = yield select(getPresence);
          let acwConversations: any[] = [];
          if (presenceState !== undefined && presenceState !== null) {
            acwConversations =
              presenceState.presence_status.load_descriptions.filter(
                (load: any) => load.load_type === 'acw',
              );
          }
          // The agent has ACW on a conversation and need to remove it before she can be assigned to a new one.
          if (acwConversations.length > 0) {
            ((leftAction = {
              text: `Go to #${acwConversations[0].csid}`,
              action: {
                navigate: `/conversation/${acwConversations[0].csid}`,
              },
            }),
              (mainAction = {
                navigate: `/conversation/${acwConversations[0].csid}`,
              }),
              (rightAction = {
                text: 'Dismiss',
                action: function* () {
                  yield put(notificationRemoveById({ id: notificationID }));
                },
              }));
            name = `You are still in "_wrap-up time_" \n\n\n**Tip.:** Add a note to the closed conversation #${acwConversations[0].csid} to end the "_wrap-up time_"`;
            title = `You are in "wrap-up time"`;
          } else {
            name = `Have you reached the limit on open assigned conversations? \n\n\n**Tip.:** Close any of your open conversations and try again.`;
            title = 'You did not get the conversation';
          }
        } else if (acceptedBy && acceptedBy?.id !== userID) {
          // Somebody else took the conversation.
          const acceptedName = acceptedBy.name || 'Another agent';
          name = `${acceptedName} was assigned to conversation #${result.data.csid}`;
          title = 'You did not get the conversation';
        } else {
          title = 'Something went wrong';
          name = 'Assign error';

          Sentry.setContext('Data', {
            data: result.data,
            acceptedByID: JSON.stringify(acceptedBy?.id),
            status: JSON.stringify(result.data.status),
          });
          Sentry.captureMessage('Assign error', {
            level: 'error',
          });
        }
        const option = {
          id: notificationID,
          title: title,
          text: name,
          className: 'info',
          type: 'conversationOfferGivenToOther',
          icon: 'i-info',
          rightAction,
          leftAction,
          mainAction,
        };
        yield put(notificationAdd(option));
        yield call(action.meta.reject);

        yield put(resultFailedAction());
      }
    } catch (exception) {
      console.warn(exception);
      result = exception;
      yield call(action.meta.reject, exception);
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromiseResponseToTransferOfferAccepted(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result?: any) => any,
) {
  return function* (action: any) {
    let result;
    // We accepted the transfer offer.
    try {
      const channel = action.payload.channel;
      result = yield call(apiAction, action.payload);

      // Check if we got the transfer.
      if (result.data.accepted === true) {
        if (channel === 'pstnphone' || channel === 'callback') {
          const getTelephony = (state) => state.telephony;
          const telephonyState = yield select(getTelephony);
          if (telephonyState && telephonyState.currentCall) {
            yield put(
              twilioActions.twilioDisconnectCall({
                csid: telephonyState.currentCall.csid,
              }),
            ); // Disconnect current call.
          }
          if (channel === 'pstnphone') {
            yield put(
              twilioActions.twilioConnectCall({ csid: result.data.csid }),
            ); // Connect to the call to the conferrence.
          } else if (
            channel === 'callback' &&
            action.payload.conversation.is_callback_consumed === false
          ) {
            yield put(
              twilioActions.twilioMakeCallback({ csid: result.data.csid }),
            ); // Make a callback call.
          }
        }

        // Navigate to conversation
        pubSub.publish('navigate', {
          path: '/conversation/:csid',
          params: {
            path: { csid: `${result.data.csid}` },
          },
        });

        yield call(action.meta.resolve, result);
        yield put(resultSucceededAction(result));
      } else {
        // We did not get the transfer offer event though we accepted if.

        const option = {
          title: `The transfer could not be delivered`,
          text: `An issue occurred when assigning you to the tranfer conversation`,
          className: 'info',
          icon: 'i-info',
        };
        yield put(notificationAdd(option));
        yield call(action.meta.reject);
        yield put(resultFailedAction());
      }
    } catch (exception) {
      console.warn(exception);
      result = exception;
      yield call(action.meta.reject, exception);
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromiseResponseToOfferRejected(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: any) {
    let result;

    try {
      result = yield call(apiAction, action.payload);

      yield call(action.meta.resolve, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      result = exception;
      yield call(action.meta.reject, exception);
      yield put(resultFailedAction(result));
    }
  };
}

function genCloseConversationWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);

      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception: any) {
      console.warn(exception);

      if (exception.response.status === 409) {
        // We try to catch 409 error.
        const payload = {
          id: action.payload.csid,
          is_closed: true,
          closed_at: null,
          created_at: null,
          closed_by: null,
          assignments: null,
        };
        yield put(resultSucceededAction({ data: payload }));
        yield call(action.meta.resolve as any, payload);
      } else {
        // Something went wrong. We reject the promise.
        yield call(action.meta.reject as any, exception);
        result = exception;
        yield put(resultFailedAction(result));
      }
    }
  };
}

function genSubmitMessageWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    // Add the optimistic result to the ConversationState
    yield put(conversationAddSubmittedMessage(action.payload));

    try {
      result = yield call(apiAction, action.payload);

      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);

      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genWithPromiseAddProfileWithResponse(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: any;
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result;

    try {
      result = yield call(apiAction, action.payload);

      const getProfile = (state) => state.settings.profile.profile;
      const profileState = yield select(getProfile);
      if (profileState && result && result.meta) {
        result.meta.profile = profileState;
      }

      yield call(action.meta.resolve as any, result);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function genGetConversationWithPromise(
  apiAction: (action: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action: {
    payload: { csid: string; skipStoreAndWs: boolean; prefetchedResult: any };
    meta: { resolve: () => void; reject: () => void };
  }) {
    let result = action.payload.prefetchedResult ?? undefined;

    try {
      // We start with subscribe (so we minimize the possibility that we miss any events).
      if (!action.payload.skipStoreAndWs) {
        yield put(
          websocketActions.request({
            type: 'connection:subscribe',
            params: {
              type: 'conversation',
              csid: parseInt(action.payload.csid, 10),
            },
          }),
        );
      }
      const getProfile = (state) => state.settings.profile.profile;
      const profileState = yield select(getProfile);

      const getConversations = (state) => state.conversations;
      const conversationsState = yield select(getConversations);

      let oldMetaConversation = null;

      for (const metaConversation of conversationsState.metaConversations) {
        if (
          metaConversation.conversation.id === parseInt(action.payload.csid, 10)
        ) {
          oldMetaConversation = metaConversation;
          yield put(
            conversationGetBeforeSucceeded({
              metaConversation: metaConversation,
              userID: profileState.id,
            }),
          );
        }
      }

      if (!oldMetaConversation) {
        const getLastId = (state) => state.conversation.lastId;
        const lastId = yield select(getLastId);
        const currentConversation = conversationsState.metaConversations.filter(
          (c) => c.conversation.id === lastId,
        );
        if (currentConversation.length === 1) {
          const previousConversations =
            currentConversation[0].previousConversations;
          if (previousConversations) {
            for (const previousConversation of previousConversations) {
              if (
                previousConversation.conversation.id ===
                parseInt(action.payload.csid, 10)
              ) {
                oldMetaConversation = previousConversation;
                yield put(
                  conversationGetBeforeSucceeded({
                    metaConversation: previousConversation,
                    userID: profileState.id,
                  }),
                );
              }
            }
          }
        }
      }

      if (!result) {
        result = yield call(apiAction, action.payload);
      }

      if (profileState && result && result.meta) {
        result.meta.userID = profileState.id;
      }

      result.meta.csid = action.payload.csid;
      result.meta.oldMetaConversation = oldMetaConversation;

      yield call(action.meta.resolve as any, result);
      yield put(
        resultSucceededAction({
          ...result,
          skipStoreAndWs: action.payload.skipStoreAndWs || false,
        }),
      );
    } catch (exception) {
      console.warn(exception);
      // Something went wrong. We reject the promise.
      yield call(action.meta.reject as any, exception);
      result = exception;
      yield put(resultFailedAction(result));
    }
  };
}

function* genConversationEmailDeliveryFailure(action: any) {
  try {
    const {
      payload: { csid = false, failure_reason = false },
    } = action;
    if (csid) {
      if (failure_reason) {
        // update conversation with error state
        const getConversation = (state) => state.conversation;

        const conversation = yield select(getConversation);

        if (conversation?.metaConversation?.conversation?.id === csid) {
          // update conversation with error state only if the conversation is open in the viewport
          yield put(conversationGet({ csid }));
        } else {
          // show notification if the conversation isn't already open
          const notificationOptions = {
            id: csid,
            type: 'seen_enduser_' + csid,
            title: 'Message not delivered',
            text: `Message could not be delivered. (Reason: ${failure_reason})`,
            className: 'warning',
            icon: 'i-exclamation',
            closeAction: function* () {
              yield put(notificationRemoveById({ id: csid }));
            },
            mainAction: {
              navigate: `/conversation/${csid}`,
            },
            leftAction: {
              text: 'Go to conversation',
              action: function* () {
                yield put(notificationRemoveById({ id: csid }));

                pubSub.publish('navigate', {
                  path: '/conversation/:csid',
                  params: {
                    path: { csid: `${csid}` },
                  },
                });
              },
            },
            rightAction: {
              text: 'Dismiss',
              action: function* () {
                yield put(notificationRemoveById({ id: csid }));
              },
            },
          };

          yield put(notificationAdd(notificationOptions));
        }
      }
    }
  } catch (exception) {
    console.warn('genConversationEmailDeliveryFailure', exception);
  }
}
